import { useState, useEffect, useMemo } from "react";
import useRefetchOnFocus from "../../../hooks/useRefetchOnFocus";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../../Components/Common/Button/Button";
import Input from "../../../Components/Common/Input/Input";
import Select from "../../../Components/Common/Select/Select";
import Table from "../../../Components/Table/Table";
import ProjectTableSkeleton from "../../../Components/Common/Skeleton/ProjectTableSkeleton";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import FilterDropdown from "../../../Components/Common/FilterDropdown/FilterDropdown";
import "./AdminDashboard.scss";
import { formatDate } from "../../../utils/Common/Date";
import useGetAdminProjects, {
  AdminProject,
  ProjectFilters,
} from "../../../hooks/useGetAdminProjects";
import SearchIcon from "Assets/Img/SearchIcon";
import MapIcon from "Assets/Img/MapIcon";
import ListIcon from "Assets/Img/ListIcon";
import DashboardCommercialIcon from "Assets/Img/DashboardCommercialIcon";
import DashboardResidentialIcon from "Assets/Img/DashboardResidentialIcon";
import NetworkError from "Components/Common/NetworkError/NetworkError";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import ProjectMapView from "Components/Common/ProjectMapView/ProjectMapView";
import EmptyProjectsIcon from "Assets/Img/EmptyProjectsIcon";
import { markProjectComplete } from "../../../services/projectService";
import { useToast } from "../../../Components/Common/Toast/ToastContext";
import CustomModal from "../../../Components/Common/CustomModal/CustomModal";
import ProjectStageModal, {
  isGCPendingStage,
} from "Components/Common/ProjectStageModal/ProjectStageModal";
import SortIcon from "Assets/Img/SortIcon";

const LIMIT = 10;

type DashboardFilters = ProjectFilters & { [key: string]: string };
const EMPTY_FILTERS: DashboardFilters = {
  status: "",
  type: "",
  lenderCompanyName: "",
};

const FILTER_LABEL_MAP: Record<string, string> = {
  active: "Active",
  inActive: "Inactive",
  pre_construction: "Pre-construction",
  draw_request: "Draw Request",
  completed: "Completed",
  cancelled: "Cancelled",
  commercial: "Commercial",
  residential: "Residential",
};

const sortOptions = [
  { label: "Sort: Added date (newest)", value: "date_desc" },
  { label: "Sort: Added date (oldest)", value: "date_asc" },
  { label: "Project name (A–Z)", value: "name_asc" },
  { label: "Status", value: "status_asc" },
];

const SORT_MAP: Record<string, { sortBy: string; sortOrder: string }> = {
  date_desc: { sortBy: "createdAt", sortOrder: "DESC" },
  date_asc: { sortBy: "createdAt", sortOrder: "ASC" },
  name_asc: { sortBy: "name", sortOrder: "ASC" },
  status_asc: { sortBy: "status", sortOrder: "ASC" },
};

export default function AdminDashboard() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<DashboardFilters>(EMPTY_FILTERS);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get("view") === "map" ? "map" : "list") as
    | "list"
    | "map";
  const setView = (v: "list" | "map") =>
    setSearchParams(
      (prev) => {
        prev.set("view", v);
        return prev;
      },
      { replace: true },
    );
  const [filterOpen, setFilterOpen] = useState(false);
  const [completing, setCompleting] = useState<Set<string>>(new Set());
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [stageModalProject, setStageModalProject] = useState<{
    name: string;
    stage: string;
  } | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const navigate = useNavigate();
  const showToast = useToast();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);
  useEffect(() => {
    setPage(1);
  }, [sort]);
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const { sortBy, sortOrder } = SORT_MAP[sort] ?? SORT_MAP.date_desc;

  const { projects, pagination, loading, error, refetch } = useGetAdminProjects(
    page,
    LIMIT,
    debouncedSearch,
    sortBy,
    sortOrder,
    filters,
  );

  const { projects: mapProjects, loading: mapLoading } = useGetAdminProjects(
    page,
    LIMIT,
    debouncedSearch,
    sortBy,
    sortOrder,
    filters,
    view === "map",
  );

  useRefetchOnFocus(refetch);

  const handleMarkComplete = async (projectId: string) => {
    setCompleting((prev) => new Set(prev).add(projectId));
    try {
      const { error } = (await markProjectComplete(projectId)) as {
        data: unknown;
        error: { message?: string } | null;
      };
      if (error) {
        showToast(
          error.message ?? "Failed to mark project complete. Please try again.",
        );
        return;
      }
      showToast("Project marked as complete.");
      refetch();
    } finally {
      setCompleting((prev) => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  };

  const handleConfirmComplete = async () => {
    if (!selectedProjectId) return;
    await handleMarkComplete(selectedProjectId);
    setConfirmModalOpen(false);
    setSelectedProjectId(null);
  };

  const handleCancelComplete = () => {
    setConfirmModalOpen(false);
    setSelectedProjectId(null);
  };

  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) => {
        const nameA = (a.name ?? "").toLowerCase();
        const nameB = (b.name ?? "").toLowerCase();
        return sortDir === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }),
    [projects, sortDir],
  );

  const bankOptions = [...new Set(projects.map((p) => p.bank).filter(Boolean))];

  const activeChips = Object.entries(filters)
    .filter(([, value]) => value !== "")
    .map(([group, value]) => ({ group, value }));

  const removeChip = (group: string) => {
    setFilters((f) => ({ ...f, [group]: "" }) as DashboardFilters);
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const projectColumns = [
    {
      header: (
        <>
          PROJECTS{" "}
          <span
            className="sorted-icon"
            onClick={(e) => {
              e.stopPropagation();
              setSortDir((d) => (d === "asc" ? "desc" : "asc"));
            }}
          >
            <SortIcon direction={sortDir} />
          </span>
        </>
      ),
      render: (data: AdminProject) => (
        <div className="pc">
          <div className="thumb">
            {data?.type === "Commercial" ? (
              <DashboardCommercialIcon />
            ) : (
              <DashboardResidentialIcon />
            )}
          </div>
          <div className="pi">
            <div className="pname" title={data?.name}>
              {data?.name}
            </div>
            <div className="ptype">{data?.type}</div>
            <div className="plinks">
              <span className="pl">{data?.status}</span>
              <span className="pl">AIA Summary</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      header: "STATUS",
      render: (data: AdminProject) => (
        <div className="sc">
          <div
            className={`badge ${
              data?.status === "Active"
                ? "activeBadge"
                : data?.status === "Needs review"
                  ? "reviewBadge"
                  : "preConstBadge"
            }`}
          >
            <div className="bdot" />
            <span>{data?.status}</span>
          </div>
          <div className="ssub">
            {formatDate(data?.estCompletionDate ?? "")}
          </div>
        </div>
      ),
    },
    {
      header: "PROGRESS",
      render: (data: AdminProject) => (
        <div className="pgc">
          <div className="pgt">
            <div
              className={`pgf ${
                data?.status === "Active"
                  ? "activeProgress"
                  : data?.status === "Needs review"
                    ? "reviewProgress"
                    : "preConstProgress"
              }`}
              style={{ width: `${data?.progress}%` }}
            />
          </div>
          <div className="pgn">{Math.round(data?.progress || 0)}%</div>
        </div>
      ),
    },
    {
      header: "BANK / DRAWS",
      render: (data: AdminProject) => (
        <div className="bnk">
          <div className="bn">{data?.bank || "—"}</div>
          <div className="dl">{data?.lastDrawNumber ?? 0} draws · view</div>
        </div>
      ),
    },
    {
      header: "ACTIONS",
      render: (data: AdminProject) => (
        <div className="ac">
          <Button
            className="ab"
            onClick={() => {
              if (isGCPendingStage(data?.projectStage)) {
                setStageModalProject({
                  name: data?.name,
                  stage: data?.projectStage,
                });
                return;
              }
              navigate(
                data?.projectStage === "admin_pre_construction_authoring"
                  ? `/precon-authoring/${data?.id}`
                  : data?.projectStage === "admin_construction_report" &&
                      data?.status === "Draw Request"
                    ? `/construction-report-authoring/${data?.id}/${data?.drawId}`
                    : `/project-detail/${data?.id}`,
              );
            }}
          >
            View / Edit
          </Button>
          {data?.projectStage !== "complete" &&
            (() => {
              const canMarkComplete =
                data?.status === "Active" &&
                data?.projectStage === "construction";
              return (
                <Button
                  className="ab ab-ok"
                  disabled={!canMarkComplete || completing.has(data?.id)}
                  title={
                    !canMarkComplete
                      ? "Project must be Active and in Construction stage before it can be marked complete."
                      : undefined
                  }
                  onClick={() => {
                    if (!canMarkComplete) return;
                    setSelectedProjectId(data?.id);
                    setConfirmModalOpen(true);
                  }}
                >
                  Mark complete
                </Button>
              );
            })()}
        </div>
      ),
    },
  ];

  return (
    <div className="shell adminDashoard">
      <div className="content">
        <div className="phdr">
          <span className="ptitle">Projects</span>
          <span className="psub">Active · sorted by added date</span>
        </div>

        <div className="tbar">
          <div className="srch">
            <Input
              icon={<SearchIcon />}
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="vtog">
            <Button
              className={`vb${view === "list" ? " on" : ""}`}
              onClick={() => setView("list")}
            >
              <ListIcon /> List
            </Button>
            <Button
              className={`vb${view === "map" ? " on" : ""}`}
              onClick={() => setView("map")}
            >
              <MapIcon /> Map
            </Button>
          </div>
          {view === "list" && (
            <Select
              className="ssel"
              options={sortOptions}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            />
          )}
          <div className="fwrap">
            <Button
              className={`fbtn${filterOpen || activeChips.length > 0 ? " fbtn--active" : ""}`}
              onClick={() => setFilterOpen((o) => !o)}
            >
              Filters{activeChips.length > 0 ? ` (${activeChips.length})` : ""}
            </Button>
            <FilterDropdown
              open={filterOpen}
              filters={filters}
              onChange={setFilters}
              onClose={() => setFilterOpen(false)}
              bankOptions={bankOptions}
            />
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="chips">
            {activeChips.map(({ group, value }) => (
              <div
                key={group}
                className="chip"
                onClick={() => removeChip(group)}
              >
                {FILTER_LABEL_MAP[value] ?? value} ×
              </div>
            ))}
            <span className="ca" onClick={clearFilters}>
              Clear all
            </span>
          </div>
        )}

        {view === "map" ? (
          <ProjectMapView
            projects={mapProjects}
            loading={mapLoading}
            onProjectClick={(project) => {
              if (isGCPendingStage(project.projectStage)) {
                setStageModalProject({
                  name: project.name,
                  stage: project.projectStage,
                });
                return;
              }
              navigate(
                project.projectStage === "admin_pre_construction_authoring"
                  ? `/precon-authoring/${project.id}`
                  : project.projectStage === "admin_construction_report" &&
                      project.status === "Draw Request"
                    ? `/construction-report-authoring/${project.id}/${project.drawId}`
                    : `/project-detail/${project.id}`,
              );
            }}
          />
        ) : loading ? (
          <ProjectTableSkeleton count={6} />
        ) : error ? (
          <NetworkError />
        ) : projects.length === 0 ? (
          <EmptyState
            height={"calc(100vh - 173px)"}
            icon={<EmptyProjectsIcon />}
            title={
              debouncedSearch
                ? `No results for "${debouncedSearch}"`
                : activeChips.length > 0
                  ? "No projects match your filters"
                  : "No Projects Yet"
            }
            subtitle={
              debouncedSearch
                ? "Try a different search term or clear the search to see all projects."
                : activeChips.length > 0
                  ? "Try adjusting or clearing the active filters to see more results."
                  : "Projects added to the platform will appear here once they are created."
            }
          />
        ) : (
          <>
            <Table<AdminProject>
              data={sortedProjects}
              columns={projectColumns}
            />
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={LIMIT}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
      <ProjectStageModal
        show={!!stageModalProject}
        onClose={() => setStageModalProject(null)}
        projectName={stageModalProject?.name}
        projectStage={stageModalProject?.stage}
      />
      <CustomModal
        show={confirmModalOpen}
        handleClose={handleCancelComplete}
        title="Mark Project Complete?"
        isCrossBtn={false}
        footerContent={
          <>
            <Button
              className="btn btn-secondary"
              onClick={handleCancelComplete}
              disabled={
                selectedProjectId ? completing.has(selectedProjectId) : false
              }
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              onClick={handleConfirmComplete}
              disabled={
                selectedProjectId ? completing.has(selectedProjectId) : false
              }
            >
              {selectedProjectId && completing.has(selectedProjectId)
                ? "Saving…"
                : "Confirm"}
            </Button>
          </>
        }
      >
        Are you sure you want to mark this project as complete? This action will
        move the project to completed status.
      </CustomModal>
    </div>
  );
}
